import { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import {
  Dashboard,
  Business,
  ShoppingCart,
  AccountBalance,
  People,
  BarChart,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Settings,
} from "@mui/icons-material";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const HorizontalNavbar = () => {
  const history = useHistory();
  const location = useLocation();
  const { permissions } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [nestedAnchorEl, setNestedAnchorEl] = useState(null);
  const [activeNestedMenu, setActiveNestedMenu] = useState(null);

  const hasReadMasterPermission = permissions.includes('Read Master');
  const hasReadTransactionPermission = permissions.includes('Read Transaction');
  const hasReadAccountsPermission = permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = permissions.includes('Read Administration');
  const hasReadReportsPermission = permissions.includes('Read Reports');

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNestedMenuOpen = (event, menuLabel) => {
    event.stopPropagation();
    // Always set the new anchor and menu - this handles switching between submenus
    setNestedAnchorEl(event.currentTarget);
    setActiveNestedMenu(menuLabel);
  };

  const handleNestedMenuClose = () => {
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMenuClose();
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard sx={{ fontSize: 18 }} />,
      route: '/Home',
      permission: true,
    },
    {
      id: 'master',
      label: 'Master',
      icon: <Business sx={{ fontSize: 18 }} />,
      permission: hasReadMasterPermission,
      subItems: [
        { label: 'Company', route: '/CompanyEdit' },
        { label: 'Client', route: '/clientIndex' },
        { label: 'Vendor', route: '/vendorIndex' },
        { label: 'Account Head', route: '/accountHeadIndex' },
        { label: 'Bank', route: '/bankIndex' },
        { label: 'Category', route: '/categoryIndex' },
        { label: 'Item', route: '/itemIndex' },
      ],
    },
    {
      id: 'event',
      label: 'Rental',
      icon: <People sx={{ fontSize: 18 }} />,
      permission: hasReadTransactionPermission || hasReadMasterPermission,
      subItems: [
        { label: 'Product Enquiry', route: '/EnquiryIndex' },
        { label: 'Rental Enquiry', route: '/EventItemEnquiryIndex' },
        { label: 'Estimate', route: '/EstimateIndex' },
        { label: 'Delivery-Out', route: '/DeliveryIndex' },
        { label: 'Delivery-In', route: '/DeliveryInIndex' },
        { label: 'Rent Invoice', route: '/RentInvoiceIndex' },
      ],
    },
    {
      id: 'transaction',
      label: 'Transaction',
      icon: <ShoppingCart sx={{ fontSize: 18 }} />,
      permission: hasReadTransactionPermission || hasReadMasterPermission,
      subItems: [
        { label: 'Purchase', route: '/PurchaseIndex' },
        { label: 'Sales', route: '/SaleIndex' },
      ],
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: <AccountBalance sx={{ fontSize: 18 }} />,
      permission: hasReadAccountsPermission,
      subItems: [
        { label: 'Payment Receipts', route: '/PaymentReceiptsIndex' },
        { label: 'Journal Entry', route: '/JournalEntryIndex' },
      ],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <BarChart sx={{ fontSize: 18 }} />,
      permission: hasReadReportsPermission || hasReadMasterPermission,
      subItems: [
        { label: 'Stock', route: '/StockReportIndex' },
        { label: 'Item Forecast Report', route: '/ItemForecastReport' },
        { label: 'Estimate Report', route: '/MonthlyEstimateReport' },
        { label: 'Delivery Report', route: '/DeliverySummaryReport' },
        { label: 'Delivery Stock', route: '/DeliveryStockReport' },
        { label: 'Purchase Party & ItemWise Report', route: '/PurchasePartyandItemWiseReport' },
        { label: 'Sale Party & ItemWise Report', route: '/SalePartyandItemWiseReport' },
        { label: 'Rent Invoice Party & ItemWise Report', route: '/RentInvoicePartyandItemWiseReport' },
        { label: 'Ledger', route: '/LedgerReport' },
        { label: 'CashBook', route: '/CashBook' },
        { label: 'Customer Due List', route: '/CustomerDueListReport' },
        { label: 'Vendor Due List', route: '/VendorDueListReport' },
      ],
    },
    {
      id: 'auth',
      label: 'Auth',
      icon: <Settings sx={{ fontSize: 18 }} />,
      permission: hasReadAdministrationPermission,
      subItems: [
        { label: 'Users', route: '/UserIndex' },
        { label: 'Roles', route: '/RoleIndex' },
        { label: 'Reset Password', route: '/PasswordChange' },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings sx={{ fontSize: 18 }} />,
      permission: hasReadMasterPermission,
      subItems: [
        { label: 'Logout', route: '/Logout' },
      ],
    },
  ];

  const renderNestedMenu = (parentItem) => (
    <Menu
      anchorEl={nestedAnchorEl}
      open={activeNestedMenu === parentItem.label}
      onClose={handleNestedMenuClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          backgroundColor: '#183b4a',
          color: 'white',
          minWidth: '200px',
          maxHeight: '70vh',
          overflowY: 'auto',
          ml: 0.5,
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        },
      }}
    >
      {parentItem.subItems?.map((subItem, idx) => (
        subItem.isDivider ? (
          <Box key={idx} sx={{ my: 0.5 }}>
            <Divider sx={{ borderColor: '#FFD700', borderWidth: '1px' }} />
            <Typography
              sx={{
                color: '#FFD700',
                px: 2,
                py: 0.5,
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {subItem.label}
            </Typography>
          </Box>
        ) : (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(subItem.route)}
            sx={{
              fontSize: '13px',
              py: 0.75,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            {subItem.label}
          </MenuItem>
        )
      ))}
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '48px', gap: 0 }}>
      {menuItems.map((item) => {
        if (!item.permission) return null;
        const isCurrentSection = item.subItems?.some(child => child.route?.toLowerCase() === location.pathname.toLowerCase());

        if (item.route && !item.subItems) {
          return (
            <Box
              key={item.id}
              component={NavLink}
              to={item.route}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 1,
                color: 'white', textDecoration: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500, height: '100%',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                '&.active': { backgroundColor: 'rgba(255,255,255,0.15)' },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
            </Box>
          );
        }

        return (
          <Box key={item.id}>
            <Box
              component="button" type="button" aria-haspopup="menu" aria-expanded={activeMenu === item.id} onClick={(e) => handleMenuOpen(e, item.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 1,
                border: 0, borderRadius: '10px', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 500, height: '48px',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                ...((activeMenu === item.id || isCurrentSection) && { backgroundColor: 'rgba(255,255,255,0.15)', boxShadow: 'inset 0 -2px #71d9bd' }),
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
              <KeyboardArrowDown sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={activeMenu === item.id}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  backgroundColor: '#183b4a', color: 'white', minWidth: '180px',
                  mt: 0, borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                },
              }}
            >
              {item.subItems?.map((subItem, idx) => (
                subItem.isNested ? (
                  <Box key={idx}>
                    <MenuItem
                      onClick={(e) => handleNestedMenuOpen(e, subItem.label)}
                      sx={{
                        fontSize: '13px', py: 1, display: 'flex', justifyContent: 'space-between',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                        ...(activeNestedMenu === subItem.label && { backgroundColor: 'rgba(255,255,255,0.1)' }),
                      }}
                    >
                      {subItem.label}
                      <KeyboardArrowRight sx={{ fontSize: 16 }} />
                    </MenuItem>
                    {renderNestedMenu(subItem)}
                  </Box>
                ) : (
                  <MenuItem
                    key={idx}
                    onClick={() => handleNavigation(subItem.route)}
                    sx={{ fontSize: '13px', py: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                  >
                    {subItem.label}
                  </MenuItem>
                )
              ))}
            </Menu>
          </Box>
        );
      })}
    </Box>
  );
};

export default HorizontalNavbar;
